import React, { useCallback, useState, useMemo } from 'react';
import { useShortAnswerStats } from '../state/hooks';
import { useClassInsights } from './ClassInsightsContext';
import { statsBody } from './useFilters';
import { catalogClasses } from './catalogSelectors';
import { PanelHeader } from './PanelHeader';
import { PanelIconButton } from './PanelIconButton';
import { ExportFormatDropdown } from './ExportFormatDropdownNew';
import { ExpandIcon } from './Icons';
import { ShortAnswerModal } from './ShortAnswerModal';
import { ToolEmptyState } from './EmptyState';
import DropdownMenu from '../DropdownMenu';
import SmallLoader from '../Loader/SmallLoader';
import { getScoreCellClass, getRankClass, getAvgColor, STUDENT_SORT_OPTIONS, DEFAULT_STUDENT_SORT, rankAndSortStudents } from './utils';
import { SECTIONS } from './DownloadManager';
import DownLoad from '../../static/img/Download.svg';
import shortAnswerIcon from '../../static/icons/ShortAnsIcon.svg';
import classAvgIcon from '../../static/icons/ClassAvgIcon.svg';

export function ShortAnswerPanel({
  mode = 'small',
  onExpand,
  onCollapse,
  open = true,
  onExited,
  exportMode = false,
}) {
  const { filters, debouncedFilters, setFilters, catalog } = useClassInsights();
  const [sortBy, setSortBy] = useState(DEFAULT_STUDENT_SORT);

  // JED-1066: empty classIds means "all classes", not "no classes". Gate the
  // query on whether the catalog has any class at all -- mirroring
  // KpiSummaryCards -- so selecting "All Classes" still loads this report
  // instead of leaving the panel blank while the KPIs show data.
  const hasClassScope = catalogClasses(catalog).length > 0;
  const { data, isFetching: isTileLoading } = useShortAnswerStats(
    { ...tileBody(debouncedFilters, debouncedFilters.shortAnswerIds), sortBy: 'performance', sortOrder: 'desc' },
    hasClassScope
  );

  const list = data?.options || [];
  const stats = data?.stats || null;


  const getSelectedIds = () => {
    if (list.length === 0) return [];
    if (!Array.isArray(filters.shortAnswerIds) || filters.shortAnswerIds.length === 0) return [];

    const validIds = [...new Set(filters.shortAnswerIds.map(String))]
      .filter((id) => list.some((item) => String(item.id) === id));

    if (validIds.length > 0 && validIds.length !== list.length) {
      return validIds;
    }

    return [];
  };
  const selectedIds = getSelectedIds();

  // Clean up stale URL IDs when scope changes. Skip when URL is empty (don't seed).
  const currentFilterIds = (filters.shortAnswerIds || []).map(String).join(',');
  const validFilterIds = selectedIds.join(',');
  if (list.length > 0 && currentFilterIds && currentFilterIds !== validFilterIds) {
    setFilters({ shortAnswerIds: selectedIds });
  }

  const onChange = useCallback((ids) => {
    const validIds = [...new Set((ids || []).map(String))]
      .filter((id) => list.some((item) => String(item.id) === id));
    setFilters({
      shortAnswerIds: validIds.length === 0 || validIds.length === list.length ? [] : validIds,
    });
  }, [setFilters, list]);
  const dropdownUiProps = {
    triggerFontSize: '16px',
    triggerPadding: '10px 16px',
  };

  const { students, headers, classAverage } = useMemo(() => {
    if (!stats || !stats.tasks || stats.tasks.length === 0) {
      return { students: [], headers: [], classAverage: 0 };
    }

    const headers = stats.tasks.map((task) => {
      const questionNumbers = Array.from(new Set(
        task.studentStats.flatMap(s =>
          s.entries
            .map((entry) => Number(entry?.questionSerialNumber?.value))
            .filter((num) => Number.isFinite(num) && num > 0)
        )
      )).sort((a, b) => a - b);

      return {
        taskId: task.taskId,
        taskTitle: task.taskTitle,
        questionNumbers,
      };
    });

    const studentMap = new Map();

    stats.tasks.forEach((task) => {
      task.studentStats.forEach((studentStat) => {
        if (!studentMap.has(studentStat.studentId)) {
          studentMap.set(studentStat.studentId, {
            studentId: studentStat.studentId,
            name: studentStat.studentName,
            scoresByTask: new Map(),
            backendAvg: studentStat.average,
          });
        }

        const student = studentMap.get(studentStat.studentId);
        const scoresByQuestion = new Map();

        studentStat.entries.forEach((entry) => {
          const questionNumber = Number(entry?.questionSerialNumber?.value);
          if (!Number.isFinite(questionNumber) || questionNumber <= 0) return;
          const awardedMarks = entry.awardMarks || entry.awardedMarks;
          scoresByQuestion.set(questionNumber, `${awardedMarks}/${entry.highestMarks}`);
        });

        student.scoresByTask.set(task.taskId, scoresByQuestion);
      });
    });

    const students = Array.from(studentMap.values()).map((student) => {
      const allScores = [];

      stats.tasks.forEach((task) => {
        const scoresByQuestion = student.scoresByTask.get(task.taskId);
        if (!scoresByQuestion) return;
        allScores.push(...scoresByQuestion.values());
      });

      return {
        studentId: student.studentId,
        name: student.name,
        scores: allScores,
        scoresByTask: student.scoresByTask,
        avg: `${student.backendAvg}%`,
      };
    });

    return {
      students,
      headers,
      classAverage: stats.averages?.classAverage || 0,
    };
  }, [stats]);

  const sortedStudents = useMemo(() => rankAndSortStudents(students, sortBy), [students, sortBy]);

  const csv = sortedStudents.map((s) => {
    const row = {
      Rank: s.rank,
      Student: s.name,
    };

    headers.forEach((header) => {
      const scoresByQuestion = s.scoresByTask.get(header.taskId);
      if (scoresByQuestion) {
        header.questionNumbers.forEach((qNum) => {
          const score = scoresByQuestion.get(qNum);
          const label = `${header.taskTitle} - Q${qNum}`;
          row[label] = score || '-';
        });
      }
    });

    row.Average = s.avg;
    return row;
  });

  if (mode === 'expanded') {
    return (
      <ShortAnswerModal
        open={open}
        onClose={onCollapse}
        onExited={onExited}
        list={list}
        selectedIds={selectedIds}
        onChange={onChange}
        stats={stats}
        isLoading={isTileLoading}
        sortBy={sortBy}
        onSortChange={setSortBy}
        exportMode={exportMode}
      />
    );
  }

  return (
    <section className="ci-panel ci-table-panel" data-section="short-answer">
      <PanelHeader title="Short Answer">
        <DropdownMenu
          multiselect
          options={list}
          selectedValues={selectedIds}
          onChange={onChange}
          placeholder="All"
          getOptionLabel={(opt) => opt.title || opt.name}
          getOptionValue={(opt) => String(opt.id)}
          alwaysWhite
          showAllOption={true}
          showAllWhenAllSelected={true}
          leadingIcon={shortAnswerIcon}
          compactDisplay
          triggerWidth="clamp(120px, 40vw, 350px)"
          {...dropdownUiProps}
        />
        <ExportFormatDropdown
          section={SECTIONS.SHORT_ANSWER}
          data={stats}
          csvData={csv}
          isModal={false}
          trigger={
            <PanelIconButton aria-label="Download">
              <img src={DownLoad} className="ci-download-icon" alt="" />
            </PanelIconButton>
          }
        />
        <PanelIconButton
          type="button"
          onClick={onExpand}
          aria-label="Expand"
          className="ci-icon-button-expand"
        >
          <ExpandIcon />
        </PanelIconButton>
      </PanelHeader>
      {!isTileLoading && sortedStudents.length > 0 && (
        <div className="ci-sort-controls">
          <span className="ci-sort-label">Sort by:</span>
          <DropdownMenu
            multiselect
            singleSelect
            options={STUDENT_SORT_OPTIONS}
            selectedValues={[sortBy]}
            onChange={(values) => setSortBy(values[0] || STUDENT_SORT_OPTIONS[0])}
            placeholder="Sort"
            getOptionLabel={(opt) => opt}
            getOptionValue={(opt) => opt}
            alwaysWhite
            compactDisplay
            showCheckboxes={false}
            showAllOption={false}
            triggerFontSize="14px"
            triggerPadding="8px 12px"
            triggerWidth="auto"
            triggerMinWidth="280px"
          />
          <div className="ci-modal-class-avg ci-panel-class-avg">
            <img src={classAvgIcon} alt="" className="ci-modal-class-avg-icon" />
            Class average: <span>{classAverage}%</span>
          </div>
        </div>
      )}
      <div className="ci-table-wrap ci-short-answer-panel-wrap">
        {isTileLoading ? (
          <div className="ci-panel-loading">
            <SmallLoader />
          </div>
        ) : sortedStudents.length === 0 ? (
          <ToolEmptyState tool="shortAnswer" />
        ) : (
          <table className="ci-table ci-short-answer-panel-table">
            <thead>
              <tr>
                <th className="ci-sticky-col">Student</th>
                {headers.length > 0 && <th className="ci-group-separator-cell" aria-hidden="true" />}
                {headers.map((header, headerIndex) => (
                  <React.Fragment key={header.taskId}>
                    <th colSpan={Math.max(1, header.questionNumbers.length)} className="ci-task-group-header">
                      {header.taskTitle}
                    </th>
                    {headerIndex < headers.length - 1 && (
                      <th className="ci-group-separator-cell" aria-hidden="true" />
                    )}
                  </React.Fragment>
                ))}
                {headers.length > 0 && <th className="ci-group-separator-cell" aria-hidden="true" />}
                <th className="ci-sticky-right ci-avg-header">Average</th>
              </tr>
            </thead>
            <tbody>
              {sortedStudents.map((student) => (
                <tr key={student.studentId}>
                  <td className="ci-sticky-col">
                    <div className="ci-table-student">
                      <span className={getRankClass(student.rank)}>{student.rank}</span>
                      {student.name}
                    </div>
                  </td>
                  {headers.length > 0 && <td className="ci-group-separator-cell" aria-hidden="true" />}
                  {headers.map((header, headerIndex) => {
                    const scoresByQuestion = student.scoresByTask.get(header.taskId);
                    const questionColumns = header.questionNumbers.length > 0 ? header.questionNumbers : [null];
                    return (
                      <React.Fragment key={`${student.studentId}-${header.taskId}`}>
                        {questionColumns.map((questionNumber, questionIndex) => {
                          const score = questionNumber == null ? '-' : (scoresByQuestion?.get(questionNumber) || '-');
                          const classes = [getScoreCellClass(score), 'ci-score-cell'];
                          if (questionIndex === 0) classes.push('ci-score-cell--group-start');
                          if (questionIndex === questionColumns.length - 1) classes.push('ci-score-cell--group-end');
                          return (
                            <td
                              key={`${header.taskId}-${questionNumber ?? 'q'}`}
                              className={classes.join(' ')}
                            >
                              {score}
                            </td>
                          );
                        })}
                        {headerIndex < headers.length - 1 && (
                          <td className="ci-group-separator-cell" aria-hidden="true" />
                        )}
                      </React.Fragment>
                    );
                  })}
                  {headers.length > 0 && <td className="ci-group-separator-cell" aria-hidden="true" />}
                  <td
                    className="ci-cell-avg ci-sticky-right"
                    style={{ backgroundColor: getAvgColor(parseInt(student.avg, 10)) }}
                  >
                    {student.avg}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

const tileBody = (filters, ids) => ({
  ...statsBody(filters),
  markingToolIds: ids && ids.length > 0 ? ids : null,
});
